import { useState } from "react";
import { Button, Spinner } from "flowbite-react";
import { FaApple, FaFacebook, FaGoogle } from "react-icons/fa";
import { useAuthentication, useToast, useTranslation } from "@repo/core";
import { useGoogleSignIn } from "../../hooks/useGoogleSignIn";
import { useFacebookSignIn } from "../../hooks/useFacebookSignIn";
import { useAppleSignIn } from "../../hooks/useAppleSignIn";
import {
  isAppleLoginEnabled,
  isFacebookLoginEnabled,
  isGoogleLoginEnabled,
} from "../../config/featureFlags";
import { extractErrorMessage } from "../../utils/authErrors";

/** Google, Facebook and Apple, all three wired to the real backend. Each is
 * gated by its flag in config/featureFlags. Shared by Login and Signup. */
const SocialLoginButtons = () => {
  const { t } = useTranslation();
  const { loginWithGoogle, loginWithFacebook, loginWithApple } =
    useAuthentication();
  const { addErrorMessage } = useToast();
  // Tracks which provider's popup is in flight, if any - lets each button
  // show its own spinner instead of both reacting to a single shared flag.
  const [signingInProvider, setSigningInProvider] = useState<
    "google" | "facebook" | "apple" | null
  >(null);

  const { triggerGoogleSignIn, isReady: isGoogleReady } = useGoogleSignIn(
    async (accessToken) => {
      setSigningInProvider("google");
      try {
        await loginWithGoogle(accessToken);
      } catch (error) {
        console.error("Google sign-in failed:", error);
        addErrorMessage(extractErrorMessage(error) ?? t("login.googleSignInFailed"));
      } finally {
        setSigningInProvider(null);
      }
    }
  );

  const { triggerFacebookSignIn, isReady: isFacebookReady } = useFacebookSignIn(
    async (accessToken) => {
      setSigningInProvider("facebook");
      try {
        await loginWithFacebook(accessToken);
      } catch (error) {
        console.error("Facebook sign-in failed:", error);
        addErrorMessage(extractErrorMessage(error) ?? t("login.facebookSignInFailed"));
      } finally {
        setSigningInProvider(null);
      }
    }
  );

  const { triggerAppleSignIn, isReady: isAppleReady } = useAppleSignIn(
    async (authorizationCode) => {
      setSigningInProvider("apple");
      try {
        await loginWithApple(authorizationCode);
      } catch (error) {
        console.error("Apple sign-in failed:", error);
        addErrorMessage(extractErrorMessage(error) ?? t("login.appleSignInFailed"));
      } finally {
        setSigningInProvider(null);
      }
    },
    // Apple's own popup can fail before any code is issued - Google and
    // Facebook surface that through their callbacks, Apple through a
    // rejection, so it needs its own reporting path.
    (error) => {
      console.error("Apple sign-in failed:", error);
      addErrorMessage(t("login.appleSignInFailed"));
    }
  );

  const isSigningIn = signingInProvider !== null;

  return (
    <div className="flex gap-2">
      {isGoogleLoginEnabled && (
        <Button
          color="light"
          className="flex-1"
          disabled={!isGoogleReady || isSigningIn}
          onClick={triggerGoogleSignIn}
          aria-label="Google"
        >
          {signingInProvider === "google" ? (
            <Spinner size="sm" />
          ) : (
            <FaGoogle className="text-red-500" size={18} />
          )}
        </Button>
      )}
      {isFacebookLoginEnabled && (
        <Button
          color="light"
          className="flex-1"
          disabled={!isFacebookReady || isSigningIn}
          onClick={triggerFacebookSignIn}
          aria-label="Facebook"
        >
          {signingInProvider === "facebook" ? (
            <Spinner size="sm" />
          ) : (
            <FaFacebook className="text-blue-600" size={18} />
          )}
        </Button>
      )}
      {isAppleLoginEnabled && (
        <Button
          color="light"
          className="flex-1"
          disabled={!isAppleReady || isSigningIn}
          onClick={triggerAppleSignIn}
          aria-label="Apple"
        >
          {signingInProvider === "apple" ? (
            <Spinner size="sm" />
          ) : (
            <FaApple size={18} />
          )}
        </Button>
      )}
    </div>
  );
};

export default SocialLoginButtons;
